import { useEffect } from 'react';
import analytics from '../services/analytics';

/**
 * Global click tracking hook using event delegation.
 *
 * Any element with a `data-track` attribute will be automatically tracked.
 * No per-component onClick wiring needed.
 *
 * Supported data attributes:
 *   data-track           - The event action (e.g., "click")
 *   data-track-category  - Event category (default: "Button")
 *   data-track-label     - Descriptive label for the event
 *   data-track-value     - Numeric value (optional)
 *
 * Usage:
 *   <button data-track="click" data-track-label="save-assessment">Save</button>
 *   <button data-track="click" data-track-label="start-interview" data-track-category="Interview">Start</button>
 */
export function useGlobalClickTracking() {
  useEffect(() => {
    const handler = e => {
      const target = e.target.closest('[data-track]');
      if (target) {
        const action = target.getAttribute('data-track');
        const category = target.getAttribute('data-track-category') || 'Button';
        const label = target.getAttribute('data-track-label') || '';
        const rawValue = target.getAttribute('data-track-value');
        const value = rawValue ? Number(rawValue) : undefined;

        analytics.trackEvent(category, action, label, value !== null ? { value } : {});
      }
    };

    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, []);
}

export default useGlobalClickTracking;
